import {
  AUTO_RETRY_DELAYS_MS,
  CONNECTION_ENDED_MESSAGE,
  GATEWAY_STATUSES,
  isConnectionFailure,
  waitBeforeRetry,
} from '@/components/agent-chat/chat-send-retry.util';
import { stopServerTurn } from '@/components/agent-chat/running-turn.util';
import { getApiBase } from '@/lib/api-base';
import { searchDidNotRun } from '@/containers/search/clu-criteria-handoff';
import {
  paidAccessNotice,
  requestPaidAccess,
  type PaidAccessNotice,
} from '@/containers/settings/billing/paid-access';
import { fetchWebtokenHeadless } from '@/store/auth-helper';
import { openCreditGate } from '@/store/features/ui.feature';
import type { PersonRecord } from '@/store/features/prospects.feature';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { BRAND } from '@/lib/brand';

/**
 * Lightweight hook for the Copilot search bar on the search landing page.
 * Sends a natural language query to the agent chat endpoint and extracts
 * the first people_search / company_search tool result (filters + results)
 * from the SSE stream.
 *
 * This is NOT the full agent chat - it's a one-shot query that returns
 * structured search results and the filters the AI interpreted.
 */

export interface CopilotSearchResult {
  results: PersonRecord[];
  total: number;
  filtersUsed: Record<string, unknown>;
  /** The six flat NL-interpreted params the people_search tool echoes back
   *  in its result (`search_params`): job_title, location, industry,
   *  seniority, company_size, company_name. This is the clean surface to map
   *  onto the page's SearchFilters — `filters_used`/`filters` carries the raw
   *  Crustdata wire conditions (or nothing) and isn't reliably populated. */
  searchParams: Record<string, unknown>;
  /** The API's server-validated page filters (camelCase SearchFilters or
   *  CompanySearchFilters keys, only those that differ from the defaults),
   *  when the tool result carries them (CLE-550). Kept as received: the
   *  consumers check them (clu-criteria-handoff) and prefer them to
   *  `searchParams`, the old-vocabulary echo the legacy mapper reads. */
  criteria?: unknown;
  /** People search only: the company-first slice of the same interpretation. */
  companyCriteria?: unknown;
  /** Finder tab the interpretation targets (people_table vs company_table). */
  kind: 'people' | 'company';
}

/** A question the page could not open as a search: its turn ran to its end
 * in the search bar's chat, which the API now lists as one of the user's Clu
 * chats. The panel opens it, so the question is answered once, not again. */
export interface CopilotSearchChat {
  kind: 'chat';
  conversationId: string;
}

export interface CopilotSearchRefusal {
  kind: 'refused';
  code: string;
  message: string;
  /** A plan refusal: the paid-access dialog's notice, as the chat opens it. */
  paidAccess?: PaidAccessNotice;
}

const REFUSAL_MESSAGES: Record<string, string> = {
  turn_in_progress:
    'Clu is still working on this request. Please wait for it to finish.',
  conversation_busy:
    'Clu is already working in this chat. Please wait for it to finish.',
  run_limit_reached:
    'Clu is already working on as many requests as this workspace allows. Retry when one finishes.',
  insufficient_credits: `You're out of credits. Top up in ${BRAND.name} to continue.`,
  credits_held:
    'Clu chats still running hold your remaining credits. Try again when they finish.',
  usage_temporarily_disabled:
    'Clu is temporarily unavailable. Please try again shortly.',
  rate_limited: 'Too many Clu requests at once. Please wait a minute.',
};

/** Refusals that clear by waiting: shown as information, not as an error. */
const BUSY_REFUSALS = new Set([
  'turn_in_progress',
  'conversation_busy',
  'run_limit_reached',
]);

/** The tools a search the page opens calls before its table: the two
 * searches whose table decides, the API's setup tools (SETUP_TOOLS in
 * agent_chat.py) and the reads prod searches make first (list_connections for
 * a CRM exclusion, get_company_profile for an ICP). Any other tool means the
 * turn is answering in chat. A search tool missing here opens that search in
 * the chat; an extra one only leaves a question waiting for its turn's end. */
const SEARCH_STEPS = new Set([
  'people_search',
  'company_search',
  'load_guide',
  'load_tools',
  'load_skill',
  'read_skill',
  'list_skills',
  'read_clu_memory',
  'recall_my_conversations',
  'list_connections',
  'get_company_profile',
]);

const record = (value: unknown): Record<string, unknown> =>
  value !== null && typeof value === 'object'
    ? (value as Record<string, unknown>)
    : {};
const text = (value: unknown) =>
  typeof value === 'string' && value ? value : undefined;

/** The API refuses before starting a turn, over HTTP or an SSE error. These
 * outcomes must never become a fresh chat turn through the no-result fallback:
 * the chat would send the same request and be refused the same way. */
function searchRefusal(
  payload: unknown,
  httpStatus?: number
): CopilotSearchRefusal | null {
  const root = record(payload);
  const envelope = record(root.error);
  const detail = record(root.detail ?? envelope.details ?? root);
  const code =
    text(detail.code) ??
    text(detail.error) ??
    text(envelope.code) ??
    text(root.code) ??
    (httpStatus === undefined ? undefined : text(root.error));
  const message =
    text(detail.message) ??
    // With no message from the raiser, the envelope repeats its own code
    // (e.g. RESOURCE_CONFLICT), which is not copy to show a customer.
    (text(envelope.message) !== text(envelope.code)
      ? text(envelope.message)
      : undefined) ??
    text(root.message) ??
    text(root.detail) ??
    (text(root.error) !== code ? text(root.error) : undefined);
  const paidAccess =
    paidAccessNotice({ data: { detail: { ...detail, code, message } } }) ??
    undefined;
  if (paidAccess)
    return {
      kind: 'refused',
      code: String(code),
      message: paidAccess.message,
      paidAccess,
    };
  if (code && Object.prototype.hasOwnProperty.call(REFUSAL_MESSAGES, code))
    return {
      kind: 'refused',
      code,
      message: message ?? REFUSAL_MESSAGES[code],
    };
  // Any other 4xx is /agent/chat refusing the request before a turn (a plan
  // gate, a message too long, the rate limit), which the chat would repeat.
  // A 401 or a 5xx may pass on a second try, so those still go to the chat.
  if (
    httpStatus !== undefined &&
    httpStatus >= 400 &&
    httpStatus < 500 &&
    httpStatus !== 401
  ) {
    const fallbackCode =
      code ?? (httpStatus === 429 ? 'rate_limited' : `http_${httpStatus}`);
    return {
      kind: 'refused',
      code: fallbackCode,
      message:
        message ??
        REFUSAL_MESSAGES[fallbackCode] ??
        "Clu couldn't take this search. Please try again.",
    };
  }
  return null;
}

/** Makes the search bar's chat one of the user's Clu chats. Best effort: if
 * it fails, the panel still opens the chat, unlisted. */
async function handOffChat(conversationId: string): Promise<CopilotSearchChat> {
  try {
    const token = await fetchWebtokenHeadless();
    await fetch(
      `${getApiBase()}/api/v1/agent/conversations/${conversationId}/hand-off`,
      { method: 'POST', headers: { Authorization: `Bearer ${token}` } }
    );
  } catch {
    // Opened unlisted.
  }
  return { kind: 'chat', conversationId };
}

/** Reads the rest of a handed-off turn's stream and drops it: the API stops
 * a search-bar turn whose reader leaves, so the reader stays to its end. */
async function readToEnd(reader: ReadableStreamDefaultReader<Uint8Array>) {
  try {
    while (!(await reader.read()).done);
  } catch {
    // The stream dropped: the API stopped the turn and saved it.
  }
}

/** Shows a refused search where the user is: busy as information, credits
 * with the credits dialog, a plan gate with the paid-access dialog. */
export function announceCopilotSearchRefusal(
  refusal: CopilotSearchRefusal,
  dispatch: (action: ReturnType<typeof openCreditGate>) => unknown
) {
  if (refusal.paidAccess) {
    requestPaidAccess(refusal.paidAccess);
    return;
  }
  if (BUSY_REFUSALS.has(refusal.code)) toast.info(refusal.message);
  else toast.error(refusal.message);
  if (refusal.code === 'insufficient_credits')
    dispatch(openCreditGate({ action: 'Clu' }));
}

export function useCopilotSearch() {
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  // The latest search's turn while it may still run. The server keeps a turn
  // going when its stream closes, so the hand-off, a new search or cancel
  // stops the rest of it: the summary after the search nobody reads.
  const turnRef = useRef<string | null>(null);
  const stopTurn = useCallback(() => {
    const clientTurnId = turnRef.current;
    turnRef.current = null;
    if (clientTurnId) void stopServerTurn(null, clientTurnId);
  }, []);

  useEffect(
    () => () => {
      abortRef.current?.abort();
      abortRef.current = null;
      stopTurn();
    },
    [stopTurn]
  );

  /** `opensOnPage`: whether the page can open a search, checked before the
   * turn is stopped. One it cannot is left to run on and goes to the chat. */
  const search = useCallback(
    async (
      query: string,
      opensOnPage?: (found: CopilotSearchResult) => boolean
    ): Promise<
      CopilotSearchResult | CopilotSearchChat | CopilotSearchRefusal | null
    > => {
      abortRef.current?.abort();
      stopTurn();
      const controller = new AbortController();
      abortRef.current = controller;

      setIsSearching(true);
      setError(null);
      // The search bar's chat, from the turn's run_started: what the panel
      // opens when the page cannot take the answer.
      let conversationId: string | null = null;

      try {
        const token = await fetchWebtokenHeadless();
        if (controller.signal.aborted)
          throw new DOMException('Search cancelled', 'AbortError');
        const url = `${getApiBase()}/api/v1/agent/chat`;

        const clientTurnId = crypto.randomUUID();
        turnRef.current = clientTurnId;
        const body = {
          message: query,
          // Not 'general': the API keeps a search-bar turn out of the chat
          // history (SEARCH_BAR_CONTEXT). The stream is cut once a search the
          // page opens lands; any other query is handed to the chat after.
          context: { type: 'search' },
          history: [],
          client_turn_id: clientTurnId,
        };

        // A question that gets no answer at all goes again by itself, at
        // once and then after 2 seconds, with the same body and so the same
        // client_turn_id: the server's execution fence runs it once (CLE-565).
        let reader: ReadableStreamDefaultReader<Uint8Array> | undefined;
        let firstChunk: Uint8Array | null = null;
        for (let attempt = 0; ; attempt += 1) {
          let failure: unknown = null;
          try {
            const response = await fetch(url, {
              method: 'POST',
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
                Accept: 'text/event-stream',
              },
              body: JSON.stringify(body),
              signal: controller.signal,
            });
            if (!response.ok) {
              // A refusal is the API declining before a turn: never resent.
              const refusal = searchRefusal(
                await response.json().catch(() => null),
                response.status
              );
              if (refusal) {
                if (turnRef.current === clientTurnId) turnRef.current = null;
                setError(refusal.message);
                return refusal;
              }
              failure = new Error(`Search failed (${response.status})`);
              if (!GATEWAY_STATUSES.includes(response.status)) throw failure;
            } else {
              reader = response.body?.getReader();
              if (!reader) throw new Error('No response stream');
              const first = await reader.read();
              if (first.done) throw new Error(CONNECTION_ENDED_MESSAGE);
              firstChunk = first.value;
              break;
            }
          } catch (error) {
            if (error === failure || !isConnectionFailure(error)) throw error;
            failure = error;
          }
          if (reader) void reader.cancel().catch(() => undefined);
          reader = undefined;
          if (
            attempt >= AUTO_RETRY_DELAYS_MS.length ||
            controller.signal.aborted
          )
            throw failure;
          await waitBeforeRetry(
            AUTO_RETRY_DELAYS_MS[attempt],
            controller.signal
          );
          if (controller.signal.aborted)
            throw new DOMException('Search cancelled', 'AbortError');
        }

        if (!reader) throw new Error('No response stream');

        const decoder = new TextDecoder();
        let buffer = '';
        let searchResult: CopilotSearchResult | null = null;
        // The first search table decides. One whose search didn't run, one
        // the page cannot open, or a tool no search calls first leaves the
        // question to the chat, which shows that turn's answer instead of
        // asking again (a second turn, and its paid tools, twice).
        let toChat = false;
        // The chat the panel opens at once, its turn still running.
        let handOff: string | null = null;
        // The API names each event on its `event:` line; the data carries no
        // type (sse.py). A `data:` line belongs to the event line before it.
        let eventName = '';

        // Read only until the people_search tool result lands. The agent
        // keeps streaming a long natural-language summary AFTER the tool call
        // ("I found 28,688 VPs of Sales... patterns worth noting..."), but the
        // landing handoff only needs the interpreted search_params. Waiting for
        // that prose just delays applying the filters + running the page
        // search, which is why interpretation felt like it "took forever".
        // Once we have the result we abort the stream below.
        while (!searchResult && !handOff) {
          const { done, value } = firstChunk
            ? { done: false as const, value: firstChunk }
            : await reader.read();
          firstChunk = null;
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (line.startsWith('event: ')) {
              eventName = line.slice(7).trim();
              continue;
            }
            if (!line.startsWith('data: ')) continue;
            const name = eventName;
            eventName = '';
            const data = line.slice(6).trim();
            if (data === '[DONE]') continue;

            try {
              const event = JSON.parse(data);
              const type = name || event.type;
              if (type === 'run_started')
                conversationId = text(event.conversation_id) ?? conversationId;
              // Only an error event can be a refusal: an answer never is.
              const refusal = type === 'error' ? searchRefusal(event) : null;
              if (refusal) {
                // The refused request owns no running turn. In particular,
                // cancel must not stop the turn the busy response refers to.
                if (turnRef.current === clientTurnId) turnRef.current = null;
                controller.abort();
                setError(refusal.message);
                return refusal;
              }

              if (
                type === 'tool_start' &&
                conversationId &&
                !SEARCH_STEPS.has(event.name)
              )
                toChat = true;
              const displayType =
                type === 'tool_result' ? event.display?.type : null;
              const isTable =
                !toChat &&
                (displayType === 'people_table' ||
                  displayType === 'company_table');
              if (isTable && searchDidNotRun(event.result || {})) {
                toChat = true;
              } else if (isTable) {
                const result = event.result || {};
                const found: CopilotSearchResult = {
                  results: Array.isArray(result.results)
                    ? result.results
                    : Array.isArray(result.people)
                      ? result.people
                      : [],
                  total: result.total || result.count || 0,
                  filtersUsed: result.filters_used || result.filters || {},
                  searchParams: result.search_params || {},
                  criteria: result.criteria,
                  companyCriteria: result.company_criteria,
                  kind: displayType === 'company_table' ? 'company' : 'people',
                };
                if (opensOnPage && !opensOnPage(found)) {
                  toChat = true;
                } else {
                  searchResult = found;
                  break; // stop scanning the rest of this chunk's lines
                }
              }
              if (toChat && conversationId) {
                handOff = conversationId;
                break;
              }
            } catch {
              // Skip non-JSON lines
            }
          }
        }

        // Cancel the rest of the stream (the trailing conversational summary)
        // so the handoff fires the moment interpretation is done, not after
        // the model finishes writing prose we discard anyway.
        if (searchResult) {
          controller.abort();
          if (turnRef.current === clientTurnId) stopTurn();
          return searchResult;
        }
        if (handOff) {
          // The chat owns the turn now: a new search, cancel or leaving the
          // page neither stops it nor cuts its stream, read to its end here.
          // The panel follows the running turn to its saved reply.
          if (abortRef.current === controller) abortRef.current = null;
          if (turnRef.current === clientTurnId) turnRef.current = null;
          void readToEnd(reader);
          const chat = await handOffChat(handOff);
          if (!abortRef.current) setIsSearching(false);
          return chat;
        }
        // The stream ended: so did the turn.
        if (turnRef.current === clientTurnId) turnRef.current = null;
        return conversationId ? await handOffChat(conversationId) : null;
      } catch (err) {
        if (controller.signal.aborted || (err as Error).name === 'AbortError')
          return null;
        // The stream dropped after the turn began: the API stopped the turn
        // and saved it, so the chat shows it (with its Continue) rather than
        // asking the question again.
        if (conversationId) {
          if (abortRef.current === controller) turnRef.current = null;
          return await handOffChat(conversationId);
        }
        const message =
          err instanceof Error ? err.message : 'Clu search failed';
        if (abortRef.current === controller) setError(message);
        return null;
      } finally {
        if (abortRef.current === controller) {
          abortRef.current = null;
          setIsSearching(false);
        }
      }
    },
    [stopTurn]
  );

  const cancel = useCallback(() => {
    abortRef.current?.abort();
    stopTurn();
    setIsSearching(false);
  }, [stopTurn]);

  return { search, cancel, isSearching, error };
}
